/**
 * Dedicated Bookkeeping page — QuickBooks-ready CSV exports.
 * Settings (toggle, frequency, delivery email) + manual send + download + send history.
 */
import { useEffect, useState } from "react";
import api, { API } from "../../lib/api";
import { CurrencyDollar, ArrowsClockwise, DownloadSimple, PaperPlaneTilt, CheckCircle, Receipt, Storefront, X } from "@phosphor-icons/react";

export default function AdminBookkeeping() {
  const [s, setS] = useState(null);
  const [log, setLog] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [saveFlash, setSaveFlash] = useState(false);

  const load = async () => {
    setLoading(true);
    const [settings, logRes, previewRes] = await Promise.all([
      api.get("/admin/settings"),
      api.get("/admin/qb-export/log").catch(() => ({ data: [] })),
      api.get("/admin/qb-export/preview").catch(() => ({ data: null })),
    ]);
    setS(settings.data);
    setLog(logRes.data || []);
    setPreview(previewRes.data || null);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    const next = { ...s, ...patch };
    setS(next);
    await api.patch("/admin/settings", patch);
    setSaveFlash(true);
    setTimeout(() => setSaveFlash(false), 1500);
  };

  const sendNow = async () => {
    if (!window.confirm(`Send a QuickBooks export now (${s.qb_export_frequency || "monthly"} window) to ${s.qb_export_email || s.admin_notify_email || "(no email set!)"}?`)) return;
    setSending(true);
    try {
      const r = await api.post("/admin/qb-export/send-now");
      alert(`Sent — period: ${r.data.label}`);
      load();
    } catch (e) {
      alert("Send failed: " + (e?.response?.data?.detail || "Check that delivery email is configured."));
    } finally {
      setSending(false);
    }
  };

  const downloadCsv = async () => {
    const token = localStorage.getItem("ms_admin_token");
    const url = `${API}/admin/qb-export/download.csv`;
    const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await resp.blob();
    const a = document.createElement("a");
    const u = URL.createObjectURL(blob);
    a.href = u;
    a.download = "mystery-served_quickbooks.csv";
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(u);
  };

  if (loading || !s) return <div className="p-10 text-sm text-ms-stone">Loading…</div>;

  const recipient = s.qb_export_email || s.admin_notify_email || "";

  return (
    <div className="p-5 md:p-12 max-w-5xl" data-testid="admin-bookkeeping">
      <p className="label-eyebrow text-ms-stone">Operations</p>
      <h1 className="serif text-3xl md:text-4xl text-ms-earth mt-2 flex items-center gap-3">
        <CurrencyDollar size={32} weight="duotone" className="text-ms-brass" /> Bookkeeping
      </h1>
      <p className="mt-2 text-sm text-ms-stone max-w-2xl">
        Automated QuickBooks-ready CSV export. Tour revenue, gift-card sales, and refunds — emailed to you on the schedule below, importable directly into QuickBooks → Banking → Upload from file.
      </p>

      {/* Schedule + delivery */}
      <section className="mt-8 bg-white border border-ms-edge rounded-2xl p-6" data-testid="bk-schedule">
        <p className="label-eyebrow text-ms-brass text-xs">Automated schedule</p>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Status">
            <button
              type="button"
              onClick={() => save({ qb_export_enabled: !s.qb_export_enabled })}
              className={`w-full py-2.5 rounded-md text-sm border transition-all ${s.qb_export_enabled ? "bg-ms-ink text-ms-cream border-ms-ink" : "bg-white text-ms-stone border-ms-edge hover:bg-ms-cream"}`}
              data-testid="bk-toggle"
            >
              {s.qb_export_enabled ? "On — sending automatically" : "Off"}
            </button>
          </Field>
          <Field label="Frequency">
            <select
              value={s.qb_export_frequency || "monthly"}
              onChange={e => save({ qb_export_frequency: e.target.value })}
              className="field-input"
              data-testid="bk-frequency"
            >
              <option value="weekly">Weekly — Monday morning, last completed week</option>
              <option value="monthly">Monthly — 1st of month, previous month</option>
            </select>
          </Field>
          <Field label="Delivery email">
            <input
              type="email"
              value={s.qb_export_email || ""}
              onChange={e => setS({ ...s, qb_export_email: e.target.value })}
              onBlur={() => save({ qb_export_email: s.qb_export_email })}
              placeholder="bookkeeping@yourdomain.com"
              className="field-input"
              data-testid="bk-email"
            />
          </Field>
        </div>
        <p className="mt-4 text-xs text-ms-stone">
          {s.qb_export_enabled ? (
            <>Next automatic send will fire {s.qb_export_frequency === "weekly" ? "next Monday morning (America/New_York)" : "on the 1st of next month (America/New_York)"}. Emails go to <b>{recipient || "(no email set!)"}</b>.</>
          ) : (
            <>Automated sending is OFF. You can still send or download manually below.</>
          )}
          {saveFlash && <span className="ml-2 inline-flex items-center gap-1 text-ms-success"><CheckCircle size={12} weight="fill" /> Saved</span>}
        </p>
      </section>

      {/* Manual actions */}
      <section className="mt-6 bg-white border border-ms-edge rounded-2xl p-6" data-testid="bk-manual">
        <p className="label-eyebrow text-ms-brass text-xs">Manual export</p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button
            type="button"
            disabled={sending}
            onClick={sendNow}
            className="inline-flex items-center gap-2 px-4 py-2 text-xs label-eyebrow bg-ms-earth text-ms-cream rounded-md hover:bg-ms-brass disabled:opacity-40"
            data-testid="bk-send-now"
          >
            <PaperPlaneTilt size={12} /> {sending ? "Sending…" : "Send export now"}
          </button>
          <button
            type="button"
            onClick={downloadCsv}
            className="inline-flex items-center gap-2 px-4 py-2 text-xs label-eyebrow border border-ms-edge text-ms-earth rounded-md hover:bg-ms-cream"
            data-testid="bk-download"
          >
            <DownloadSimple size={12} /> Download CSV (last month)
          </button>
          <p className="text-xs text-ms-stone ml-auto">
            Last sent: {s.qb_export_last_run ? (
              <span><b>{new Date(s.qb_export_last_run).toLocaleString()}</b> · {s.qb_export_last_label || "—"} · {s.qb_export_last_rows || 0} rows</span>
            ) : "never"}
          </p>
        </div>
      </section>

      {/* Preview totals */}
      {preview && (
        <section className="mt-6 bg-white border border-ms-edge rounded-2xl p-6" data-testid="bk-preview">
          <p className="label-eyebrow text-ms-brass text-xs flex items-center gap-1.5"><Receipt size={12} weight="duotone" /> Preview · {preview.label}</p>
          <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-3">
            <Kpi label="Tour revenue" v={`$${preview.totals.tour_revenue.toFixed(2)}`} testid="bk-kpi-tour" />
            <Kpi label="Gift sales" v={`$${preview.totals.gift_card_sales.toFixed(2)}`} testid="bk-kpi-gift" />
            <Kpi label="Refunds" v={`−$${preview.totals.refunds.toFixed(2)}`} testid="bk-kpi-refunds" />
            <Kpi label="Net" v={`$${preview.totals.net.toFixed(2)}`} accent testid="bk-kpi-net" />
            <Kpi label="Rows" v={preview.totals.row_count} testid="bk-kpi-rows" />
          </div>
          {preview.rows.length > 0 && (
            <div className="mt-5 overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-ms-cream/40 border-b border-ms-edge">
                  <tr>
                    <th className="text-left p-3 label-eyebrow text-[10px]">Date</th>
                    <th className="text-left p-3 label-eyebrow text-[10px]">Description</th>
                    <th className="text-right p-3 label-eyebrow text-[10px]">Amount</th>
                    <th className="text-left p-3 label-eyebrow text-[10px]">Category</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r, i) => (
                    <tr key={i} className="border-b border-ms-edge last:border-0" data-testid={`bk-preview-row-${i}`}>
                      <td className="p-3 text-ms-stone whitespace-nowrap">{r.Date}</td>
                      <td className="p-3 text-ms-earth">{r.Description}</td>
                      <td className={`p-3 text-right whitespace-nowrap ${r.Amount.startsWith("-") ? "text-ms-danger" : "text-ms-earth"}`}>{r.Amount}</td>
                      <td className="p-3 text-ms-stone">{r.Category}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {preview.preview_truncated && (
                <p className="mt-3 text-xs text-ms-stone">Showing first 50 of {preview.total_rows} rows. The downloaded/emailed CSV includes all rows.</p>
              )}
            </div>
          )}
          {preview.rows.length === 0 && (
            <p className="mt-4 text-sm text-ms-stone">No transactions in this period yet.</p>
          )}
        </section>
      )}

      {/* History */}
      <section className="mt-6 bg-white border border-ms-edge rounded-2xl p-6" data-testid="bk-history">
        <div className="flex items-center justify-between">
          <p className="label-eyebrow text-ms-brass text-xs">Send history</p>
          <button onClick={load} className="inline-flex items-center gap-1.5 text-xs text-ms-stone hover:text-ms-earth" data-testid="bk-refresh">
            <ArrowsClockwise size={12} /> Refresh
          </button>
        </div>
        {log.length === 0 ? (
          <p className="mt-3 text-sm text-ms-stone">No exports have been sent yet.</p>
        ) : (
          <table className="mt-3 w-full text-sm">
            <thead className="border-b border-ms-edge">
              <tr>
                <th className="text-left p-2.5 label-eyebrow text-[10px]">Sent at</th>
                <th className="text-left p-2.5 label-eyebrow text-[10px]">Period</th>
                <th className="text-left p-2.5 label-eyebrow text-[10px]">To</th>
                <th className="text-right p-2.5 label-eyebrow text-[10px]">Rows</th>
                <th className="text-right p-2.5 label-eyebrow text-[10px]">Net</th>
                <th className="text-left p-2.5 label-eyebrow text-[10px]">Trigger</th>
              </tr>
            </thead>
            <tbody>
              {log.map(row => (
                <tr key={row.id} className="border-b border-ms-edge last:border-0" data-testid={`bk-history-row-${row.id}`}>
                  <td className="p-2.5 text-ms-stone whitespace-nowrap">{new Date(row.sent_at).toLocaleString()}</td>
                  <td className="p-2.5 text-ms-earth">{row.label}</td>
                  <td className="p-2.5 text-ms-stone">{row.recipient}</td>
                  <td className="p-2.5 text-right text-ms-earth">{row.row_count}</td>
                  <td className="p-2.5 text-right text-ms-earth">${(row.totals?.net ?? 0).toFixed(2)}</td>
                  <td className="p-2.5 text-ms-stone">{row.trigger}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <style>{`.field-input{width:100%; border:1px solid #E4DBC7; border-radius:6px; padding:10px 12px; font-size:14px; background:#fff;} .field-input:focus{outline:none; border-color:#B8753F;}`}</style>

      {/* RESTAURANT PAYOUTS */}
      <RestaurantPayouts />
    </div>
  );
}

// ---------------- Restaurant Payouts section ----------------
function todayLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}
function daysAgo(n) {
  const d = new Date(); d.setDate(d.getDate() - n);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

function RestaurantPayouts() {
  const [start, setStart] = useState(daysAgo(30));
  const [end, setEnd] = useState(todayLocal());
  const [data, setData] = useState(null);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reconcileFor, setReconcileFor] = useState(null);

  const load = async () => {
    setLoading(true);
    const [sum, pend] = await Promise.all([
      api.get("/admin/restaurant-payouts", { params: { start, end } }),
      api.get("/admin/restaurant-payouts/pending"),
    ]);
    setData(sum.data);
    setPending(pend.data || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const apply = () => load();

  const downloadCsv = async () => {
    const token = localStorage.getItem("ms_admin_token");
    const url = `${API}/admin/restaurant-payouts/export.csv?start=${start}&end=${end}`;
    const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await resp.blob();
    const a = document.createElement("a");
    const u = URL.createObjectURL(blob);
    a.href = u;
    a.download = `mystery-served_restaurant-payouts_${start}_to_${end}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(u);
  };

  return (
    <div className="mt-12 pt-10 border-t border-ms-edge" data-testid="restaurant-payouts">
      <h2 className="serif text-3xl text-ms-earth flex items-center gap-3">
        <Storefront size={28} weight="duotone" className="text-ms-brass" /> Restaurant payouts
      </h2>
      <p className="mt-2 text-sm text-ms-stone max-w-2xl">
        Reconcile actual guest counts after each tour, then download a CSV of what's owed per restaurant. Set per-head food costs on the Restaurant Stops section of each tour's edit page.
      </p>

      {/* Pending reconciliation */}
      {pending.length > 0 && (
        <section className="mt-6 bg-white border border-ms-warn/40 rounded-2xl p-6" data-testid="rp-pending">
          <p className="label-eyebrow text-ms-warn text-xs">Awaiting reconciliation — {pending.length} past {pending.length === 1 ? "tour" : "tours"}</p>
          <table className="w-full mt-3 text-sm">
            <thead className="border-b border-ms-edge">
              <tr>
                <th className="text-left p-2.5 label-eyebrow text-[10px]">Date</th>
                <th className="text-left p-2.5 label-eyebrow text-[10px]">Tour</th>
                <th className="text-right p-2.5 label-eyebrow text-[10px]">Stops set</th>
                <th className="text-right p-2.5 label-eyebrow text-[10px]">Suggested guests</th>
                <th className="p-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {pending.map(p => (
                <tr key={p.instance_id} className="border-b border-ms-edge last:border-0" data-testid={`rp-pending-${p.instance_id}`}>
                  <td className="p-2.5 text-ms-stone whitespace-nowrap">{new Date(p.starts_at).toLocaleString(undefined, {weekday:'short', month:'short', day:'numeric', hour:'numeric', minute:'2-digit'})}</td>
                  <td className="p-2.5 text-ms-earth">{p.tour_name}</td>
                  <td className="p-2.5 text-right text-ms-stone">{p.stops_configured}</td>
                  <td className="p-2.5 text-right text-ms-earth">{p.suggested_guest_count}</td>
                  <td className="p-2.5 text-right">
                    <button
                      onClick={() => setReconcileFor(p)}
                      className="text-xs label-eyebrow bg-ms-earth text-ms-cream px-3 py-1.5 rounded-md hover:bg-ms-brass"
                      data-testid={`rp-reconcile-btn-${p.instance_id}`}
                    >
                      Mark as occurred
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {/* Date filter + actions */}
      <section className="mt-6 bg-white border border-ms-edge rounded-2xl p-6" data-testid="rp-filter">
        <p className="label-eyebrow text-ms-brass text-xs">Payout report</p>
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <label>
            <span className="label-eyebrow text-ms-stone text-[10px]">From</span>
            <input type="date" value={start} onChange={e => setStart(e.target.value)} className="block mt-1 border border-ms-edge rounded-md px-3 py-2 text-sm" data-testid="rp-start" />
          </label>
          <label>
            <span className="label-eyebrow text-ms-stone text-[10px]">To</span>
            <input type="date" value={end} onChange={e => setEnd(e.target.value)} className="block mt-1 border border-ms-edge rounded-md px-3 py-2 text-sm" data-testid="rp-end" />
          </label>
          <button onClick={apply} className="inline-flex items-center gap-2 px-4 py-2 text-xs label-eyebrow border border-ms-earth text-ms-earth rounded-md hover:bg-ms-earth hover:text-ms-cream" data-testid="rp-apply">
            <ArrowsClockwise size={12} /> Apply
          </button>
          <button onClick={downloadCsv} disabled={!data || data.rows.length === 0} className="inline-flex items-center gap-2 px-4 py-2 text-xs label-eyebrow bg-ms-earth text-ms-cream rounded-md hover:bg-ms-brass disabled:opacity-40 ml-auto" data-testid="rp-download">
            <DownloadSimple size={12} /> Download CSV
          </button>
        </div>
      </section>

      {/* Results */}
      {loading || !data ? (
        <p className="mt-6 text-sm text-ms-stone">Loading…</p>
      ) : data.summary.length === 0 ? (
        <p className="mt-6 text-sm text-ms-stone bg-white border border-ms-edge rounded-md p-6">No reconciled tour stops in this range. Mark a past tour as occurred above to populate the report.</p>
      ) : (
        <>
          {/* Per-restaurant summary */}
          <section className="mt-6 bg-white border border-ms-edge rounded-2xl overflow-hidden" data-testid="rp-summary">
            <div className="px-6 py-4 border-b border-ms-edge bg-ms-cream/40 flex items-center justify-between">
              <p className="label-eyebrow text-ms-brass text-xs">Total owed per restaurant · {data.range.start} → {data.range.end}</p>
              <p className="serif text-2xl text-ms-earth" data-testid="rp-grand-total">${data.grand_total.toFixed(2)}</p>
            </div>
            <table className="w-full text-sm">
              <thead className="border-b border-ms-edge">
                <tr>
                  <th className="text-left p-3 label-eyebrow text-[10px]">Restaurant</th>
                  <th className="text-right p-3 label-eyebrow text-[10px]">Visits</th>
                  <th className="text-right p-3 label-eyebrow text-[10px]">Guests served</th>
                  <th className="text-right p-3 label-eyebrow text-[10px]">Total owed</th>
                </tr>
              </thead>
              <tbody>
                {data.summary.map(s => (
                  <tr key={s.restaurant_name} className="border-b border-ms-edge last:border-0" data-testid={`rp-restaurant-${s.restaurant_name.replace(/\s+/g, '-')}`}>
                    <td className="p-3 text-ms-earth">{s.restaurant_name}</td>
                    <td className="p-3 text-right text-ms-stone">{s.stops_count}</td>
                    <td className="p-3 text-right text-ms-earth">{s.guests}</td>
                    <td className="p-3 text-right text-ms-earth font-medium">${s.total_owed.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          {/* Line items */}
          <section className="mt-6 bg-white border border-ms-edge rounded-2xl overflow-hidden" data-testid="rp-line-items">
            <div className="px-6 py-3 border-b border-ms-edge bg-ms-cream/40">
              <p className="label-eyebrow text-ms-stone text-xs">Line items ({data.rows.length})</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="border-b border-ms-edge">
                  <tr>
                    <th className="text-left p-2.5 label-eyebrow text-[10px]">Tour date</th>
                    <th className="text-left p-2.5 label-eyebrow text-[10px]">Tour</th>
                    <th className="text-left p-2.5 label-eyebrow text-[10px]">Stop #</th>
                    <th className="text-left p-2.5 label-eyebrow text-[10px]">Restaurant</th>
                    <th className="text-right p-2.5 label-eyebrow text-[10px]">Guests</th>
                    <th className="text-right p-2.5 label-eyebrow text-[10px]">Per head</th>
                    <th className="text-right p-2.5 label-eyebrow text-[10px]">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r, i) => (
                    <tr key={`${r.instance_id}-${r.stop_number}`} className="border-b border-ms-edge last:border-0">
                      <td className="p-2.5 text-ms-stone whitespace-nowrap">{new Date(r.tour_date).toLocaleDateString()}</td>
                      <td className="p-2.5 text-ms-earth">{r.tour_name}</td>
                      <td className="p-2.5 text-ms-stone">{r.stop_number}</td>
                      <td className="p-2.5 text-ms-earth">{r.restaurant_name}</td>
                      <td className="p-2.5 text-right text-ms-stone">{r.guests}</td>
                      <td className="p-2.5 text-right text-ms-stone">${r.per_head_cost.toFixed(2)}</td>
                      <td className="p-2.5 text-right text-ms-earth font-medium">${r.total_owed.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}

      {reconcileFor && (
        <ReconcileModal
          instance={reconcileFor}
          onClose={() => setReconcileFor(null)}
          onDone={() => { setReconcileFor(null); load(); }}
        />
      )}
    </div>
  );
}

function ReconcileModal({ instance, onClose, onDone }) {
  const [count, setCount] = useState(instance.suggested_guest_count || 0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      await api.patch(`/admin/instances/${instance.instance_id}/reconcile`, {
        actual_guest_count: Number(count) || 0,
        occurred: true,
      });
      onDone();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not save.");
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="rp-reconcile-modal">
      <form onSubmit={submit} onClick={e => e.stopPropagation()} className="bg-white border border-ms-edge rounded-md max-w-md w-full p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="label-eyebrow text-ms-brass text-xs">Mark as occurred</p>
            <h3 className="serif text-2xl text-ms-earth mt-2">{instance.tour_name}</h3>
            <p className="text-sm text-ms-stone mt-1">{new Date(instance.starts_at).toLocaleString()}</p>
          </div>
          <button type="button" onClick={onClose} className="text-ms-stone hover:text-ms-ink"><X size={18} /></button>
        </div>
        <label className="block mt-5">
          <span className="label-eyebrow text-ms-stone text-[10px]">Actual guest count</span>
          <input
            type="number"
            min="0"
            max="200"
            value={count}
            onChange={e => setCount(e.target.value)}
            className="mt-1 w-full border border-ms-edge rounded-md px-3 py-2.5 text-2xl serif text-ms-earth"
            autoFocus
            data-testid="rp-reconcile-count"
          />
          <span className="text-xs text-ms-stone mt-1.5 block">
            Suggested from confirmed bookings: <b>{instance.suggested_guest_count}</b>. Override with the actual count if walk-ins joined or no-shows reduced the party.
          </span>
        </label>
        {instance.stops_configured === 0 && (
          <p className="mt-3 text-xs text-ms-warn">⚠ This tour has no restaurant stops configured — reconciling now will produce $0 payouts. Add stops on the tour edit page first.</p>
        )}
        {error && <p className="mt-3 text-sm text-ms-danger" data-testid="rp-reconcile-error">{error}</p>}
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-xs text-ms-stone hover:text-ms-ink">Cancel</button>
          <button type="submit" disabled={saving} className="px-4 py-2 text-xs label-eyebrow bg-ms-earth text-ms-cream rounded-md hover:bg-ms-brass disabled:opacity-40" data-testid="rp-reconcile-submit">
            {saving ? "Saving…" : "Confirm"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="label-eyebrow text-ms-stone text-[10px]">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function Kpi({ label, v, accent, testid }) {
  return (
    <div className={`p-3 rounded-lg border ${accent ? "border-ms-brass bg-ms-brass/5" : "border-ms-edge"}`} data-testid={testid}>
      <p className="label-eyebrow text-ms-stone text-[10px]">{label}</p>
      <p className="serif text-2xl text-ms-earth mt-1">{v}</p>
    </div>
  );
}
